import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/public-pooja-account.css";

const API_BASE = "https://webcontrol.suvidham.in/api";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const formatPoojaDate = (date) => {
  if (!date) return "-";

  const parts = String(date).split("-");

  if (parts.length !== 3) {
    return date;
  }

  return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

export default function PoojaAccount() {
  const navigate = useNavigate();

  const token = localStorage.getItem("pooja_user_token") || "";

  const [profile, setProfile] = useState(null);
  const [orders, setOrders] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Keeps track of currently expanded booking.
  const [expandedOrder, setExpandedOrder] = useState(null);

  // =========================================================
  // AUTH FETCH
  // =========================================================

  const authFetch = (url, payload = {}) => {
    return fetch(url, {
      method: "POST",
      headers: {
        // text/plain is intentional: it keeps this cross-origin request
        // CORS-simple on IIS while the JSON body remains encrypted by HTTPS.
        "Content-Type": "text/plain;charset=UTF-8",
      },
      body: JSON.stringify({
        ...payload,
        token,
      }),
    });
  };

  // =========================================================
  // LOAD ACCOUNT
  // =========================================================

  useEffect(() => {
    if (!token) {
      navigate("/pooja-login");
      return;
    }

    const loadAccount = async () => {
      setLoading(true);
      setMessage("");

      try {
        const [profileResponse, bookingsResponse] = await Promise.all([
          authFetch(`${API_BASE}/public_profile.php`, { action: "get" }),
          authFetch(`${API_BASE}/public_my_bookings.php`, { action: "list" }),
        ]);

        const profileData = await profileResponse.json();
        const bookingData = await bookingsResponse.json();

        if (!profileResponse.ok || !bookingsResponse.ok) {
          throw new Error(
            profileData.message ||
              bookingData.message ||
              "Unable to load account"
          );
        }

        setProfile(profileData.data);
        setOrders(bookingData.data || []);
      } catch (error) {
        setMessage(error.message || "Unable to load account.");

        if (/login|session|token|unauthorized/i.test(error.message || "")) {
          localStorage.removeItem("pooja_user_token");
          localStorage.removeItem("pooja_user");

          navigate("/pooja-login");
        }
      } finally {
        setLoading(false);
      }
    };

    loadAccount();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================================================
  // PROFILE FIELD CHANGE
  // =========================================================

  const updateProfileField = (field, value) => {
    setProfile((current) => ({
      ...current,
      [field]: value,
    }));
  };

  // =========================================================
  // SAVE PROFILE
  // =========================================================

  const saveProfile = async () => {
    setMessage("");
    setSaving(true);

    try {
      const response = await authFetch(
        `${API_BASE}/public_profile.php`,
        {
          ...profile,
          action: "update",
        }
      );

      const data = await response.json();

      if (!response.ok || data.status !== "success") {
        throw new Error(data.message || "Unable to update profile");
      }

      setMessage(data.message || "Profile updated successfully.");
    } catch (error) {
      setMessage(error.message || "Unable to update profile.");
    } finally {
      setSaving(false);
    }
  };

  // =========================================================
  // LOGOUT
  // =========================================================

  const logout = async () => {
    try {
      await authFetch(`${API_BASE}/public_logout.php`, { action: "logout" });
    } catch (error) {
      // Local logout should still happen even if API fails.
    } finally {
      localStorage.removeItem("pooja_user_token");
      localStorage.removeItem("pooja_user");

      navigate("/pooja-login");
    }
  };

  // =========================================================
  // EXPAND / COLLAPSE BOOKING
  // =========================================================

  const toggleOrder = (orderNo) => {
    setExpandedOrder((current) =>
      current === orderNo ? null : orderNo
    );
  };

  // =========================================================
  // PAYMENT STATUS CLASS
  // =========================================================

  const getStatusClass = (status) => {
    return String(status || "pending")
      .toLowerCase()
      .replace(/\s+/g, "-");
  };

  // =========================================================
  // LOADING
  // =========================================================

  if (loading || !profile) {
    return (
      <div className="pa-shell">
        <div className="pa-loading-card">
          <div className="pa-loader" />

          <h3>Loading your account</h3>

          <p>{message || "Please wait..."}</p>
        </div>
      </div>
    );
  }

  // =========================================================
  // UI
  // =========================================================

  return (
    <div className="pa-page">
      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <div className="pa-topbar">
        <div className="pa-topbar-content">
          <div>
            <span className="pa-eyebrow">ONLINE POOJA BOOKING</span>

            <h1>My Account</h1>

            <p>
              Manage your profile and view your pooja booking
              history.
            </p>
          </div>

          <div className="pa-topbar-actions">
            <div className="pa-user-mobile">
              <i className="fa fa-mobile" />

              <div>
                <small>Logged in as</small>
                <strong>{profile.mobile}</strong>
              </div>
            </div>

            <button
              type="button"
              className="pa-logout"
              onClick={logout}
            >
              <i className="fa fa-sign-out" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* =====================================================
          MESSAGE
      ===================================================== */}

      {message && (
        <div className="pa-message">
          <i className="fa fa-info-circle" />
          <span>{message}</span>

          <button
            type="button"
            onClick={() => setMessage("")}
          >
            ×
          </button>
        </div>
      )}

      {/* =====================================================
          TWO COLUMN DASHBOARD
      ===================================================== */}

      <div className="pa-dashboard">
        {/* =================================================
            LEFT COLUMN - PROFILE
        ================================================= */}

        <aside className="pa-profile-column">
          <section className="pa-modern-card pa-profile-card">
            <div className="pa-card-heading">
              <div className="pa-heading-icon">
                <i className="fa fa-user" />
              </div>

              <div>
                <span>ACCOUNT</span>
                <h2>Basic Information</h2>
              </div>
            </div>

            <div className="pa-profile-avatar">
              <div className="pa-avatar-circle">
                {String(profile.name || "U")
                  .trim()
                  .charAt(0)
                  .toUpperCase()}
              </div>

              <div>
                <h3>{profile.name || "Devotee"}</h3>
                <p>{profile.mobile}</p>
              </div>
            </div>

            <div className="pa-floating-form">
              {/* NAME */}

              <div className="pa-floating-field">
                <input
                  id="profile-name"
                  type="text"
                  value={profile.name || ""}
                  placeholder=" "
                  onChange={(e) =>
                    updateProfileField(
                      "name",
                      e.target.value
                    )
                  }
                />

                <label htmlFor="profile-name">
                  Full Name
                </label>

                <i className="fa fa-user" />
              </div>

              {/* PHONE */}

              <div className="pa-floating-field disabled">
                <input
                  id="profile-phone"
                  type="text"
                  value={profile.mobile || ""}
                  placeholder=" "
                  disabled
                />

                <label htmlFor="profile-phone">
                  Mobile Number
                </label>

                <i className="fa fa-phone" />
              </div>

              {/* EMAIL */}

              <div className="pa-floating-field">
                <input
                  id="profile-email"
                  type="email"
                  value={profile.email || ""}
                  placeholder=" "
                  onChange={(e) =>
                    updateProfileField(
                      "email",
                      e.target.value
                    )
                  }
                />

                <label htmlFor="profile-email">
                  E-mail Address
                </label>

                <i className="fa fa-envelope" />
              </div>

              {/* ADDRESS */}

              <div className="pa-floating-field textarea">
                <textarea
                  id="profile-address"
                  rows="4"
                  value={profile.address || ""}
                  placeholder=" "
                  onChange={(e) =>
                    updateProfileField(
                      "address",
                      e.target.value
                    )
                  }
                />

                <label htmlFor="profile-address">
                  Address for Communication
                </label>

                <i className="fa fa-map-marker" />
              </div>

              {/* CITY */}

              <div className="pa-floating-field">
                <input
                  id="profile-city"
                  type="text"
                  value={profile.city || ""}
                  placeholder=" "
                  onChange={(e) =>
                    updateProfileField(
                      "city",
                      e.target.value
                    )
                  }
                />

                <label htmlFor="profile-city">
                  City
                </label>

                <i className="fa fa-building" />
              </div>

              {/* STATE */}

              <div className="pa-floating-field">
                <input
                  id="profile-state"
                  type="text"
                  value={profile.state || ""}
                  placeholder=" "
                  onChange={(e) =>
                    updateProfileField(
                      "state",
                      e.target.value
                    )
                  }
                />

                <label htmlFor="profile-state">
                  State
                </label>

                <i className="fa fa-map" />
              </div>

              {/* PIN */}

              <div className="pa-floating-field">
                <input
                  id="profile-pin"
                  type="text"
                  inputMode="numeric"
                  maxLength="6"
                  value={profile.pin_code || ""}
                  placeholder=" "
                  onChange={(e) =>
                    updateProfileField(
                      "pin_code",
                      e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 6)
                    )
                  }
                />

                <label htmlFor="profile-pin">
                  PIN Code
                </label>

                <i className="fa fa-map-pin" />
              </div>
            </div>

            <button
              type="button"
              className="pa-update-button"
              onClick={saveProfile}
              disabled={saving}
            >
              {saving ? (
                <>
                  <i className="fa fa-spinner fa-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <i className="fa fa-check-circle" />
                  Update Profile
                </>
              )}
            </button>
          </section>
        </aside>

        {/* =================================================
            RIGHT COLUMN - BOOKING / PAYMENT HISTORY
        ================================================= */}

        <main className="pa-history-column">
          <section className="pa-modern-card">
            <div className="pa-card-heading pa-history-heading">
              <div className="pa-heading-left">
                <div className="pa-heading-icon">
                  <i className="fa fa-history" />
                </div>

                <div>
                  <span>TRANSACTIONS</span>
                  <h2>Payment & Booking History</h2>
                </div>
              </div>

              <div className="pa-booking-count">
                {orders.length}
                <small>
                  {orders.length === 1
                    ? " Booking"
                    : " Bookings"}
                </small>
              </div>
            </div>

            {!orders.length ? (
              <div className="pa-empty-history">
                <div className="pa-empty-icon">
                  <i className="fa fa-calendar-o" />
                </div>

                <h3>No bookings found</h3>

                <p>
                  Your online pooja bookings will appear
                  here.
                </p>

                <button
                  type="button"
                  onClick={() =>
                    navigate("/online-pooja-booking")
                  }
                >
                  Book a Pooja
                </button>
              </div>
            ) : (
              <div className="pa-order-list">
                {orders.map((order, orderIndex) => {
                  const isExpanded =
                    expandedOrder === order.order_no;

                  const poojaList =
                    order.pooja_list || [];

                  return (
                    <article
                      className={`pa-order-card ${
                        isExpanded ? "expanded" : ""
                      }`}
                      key={order.order_no}
                    >
                      {/* =====================================
                          COLLAPSED ORDER SUMMARY
                      ===================================== */}

                      <div
                        className="pa-order-summary"
                        onClick={() =>
                          toggleOrder(order.order_no)
                        }
                      >
                        <div className="pa-order-number">
                          <div className="pa-order-index">
                            {orderIndex + 1}
                          </div>

                          <div>
                            <small>ORDER NUMBER</small>

                            <strong>
                              {order.order_no}
                            </strong>

                            <span>
                              <i className="fa fa-clock-o" />
                              {order.booking_datetime_display ||
                                "-"}{" "}
                              IST
                            </span>
                          </div>
                        </div>

                        <div className="pa-order-payment">
                          <span
                            className={`pa-status ${getStatusClass(
                              order.payment_status
                            )}`}
                          >
                            {order.payment_status ||
                              "PENDING"}
                          </span>

                          <strong>
                            {money(order.total_amount)}
                          </strong>
                        </div>

                        <div className="pa-order-count">
                          <small>POOJAS</small>

                          <strong>
                            {poojaList.length}
                          </strong>
                        </div>

                        {/* EYE BUTTON */}

                        <button
                          type="button"
                          className="pa-view-button"
                          title={
                            isExpanded
                              ? "Hide booking details"
                              : "View booking details"
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleOrder(
                              order.order_no
                            );
                          }}
                        >
                          <i
                            className={`fa ${
                              isExpanded
                                ? "fa-eye-slash"
                                : "fa-eye"
                            }`}
                          />

                          <span>
                            {isExpanded
                              ? "Hide"
                              : "View"}
                          </span>
                        </button>

                        <i
                          className={`fa fa-chevron-down pa-chevron ${
                            isExpanded ? "open" : ""
                          }`}
                        />
                      </div>

                      {/* =====================================
                          EXPANDED DETAILS
                      ===================================== */}

                      {isExpanded && (
                        <div className="pa-order-details">
                          <div className="pa-detail-heading">
                            <div>
                              <span>
                                BOOKING DETAILS
                              </span>

                              <h3>
                                Poojas in this booking
                              </h3>
                            </div>

                            <div className="pa-detail-total">
                              <small>
                                Total Amount
                              </small>

                              <strong>
                                {money(
                                  order.total_amount
                                )}
                              </strong>
                            </div>
                          </div>

                          {/* ===============================
                              POOJA DETAILS
                          =============================== */}

                          <div className="pa-pooja-list">
                            {poojaList.map(
                              (item, itemIndex) => (
                                <div
                                  className="pa-pooja-card"
                                  key={`${order.order_no}-${itemIndex}`}
                                >
                                  <div className="pa-pooja-number">
                                    {itemIndex + 1}
                                  </div>

                                  <div className="pa-pooja-content">
                                    <div className="pa-pooja-title">
                                      <div>
                                        <h4>
                                          {item.vazhipad_name ||
                                            "Pooja"}
                                        </h4>

                                        <p>
                                          <i className="fa fa-bank" />
                                          {item.prathishta_name ||
                                            "-"}
                                        </p>
                                      </div>

                                      <strong>
                                        {money(
                                          item.amount
                                        )}
                                      </strong>
                                    </div>

                                    <div className="pa-pooja-info-grid">
                                      {/* DEVOTEE */}

                                      <div>
                                        <small>
                                          DEVOTEE
                                        </small>

                                        <strong>
                                          {item.devotee_name ||
                                            "-"}
                                        </strong>
                                      </div>

                                      {/* BIRTH STAR */}

                                      <div>
                                        <small>
                                          BIRTH STAR
                                        </small>

                                        <strong>
                                          {item.birth_star ||
                                            "-"}
                                        </strong>
                                      </div>

                                      {/* POOJA DATE */}

                                      <div>
                                        <small>
                                          POOJA DATE
                                        </small>

                                        <strong>
                                          {formatPoojaDate(
                                            item.pooja_date
                                          )}
                                        </strong>
                                      </div>

                                      {/* QUANTITY */}

                                      <div>
                                        <small>
                                          QUANTITY
                                        </small>

                                        <strong>
                                          {item.quantity ||
                                            1}
                                        </strong>
                                      </div>

                                      {/* THIDHI */}

                                      {item.thidhi && (
                                        <div>
                                          <small>
                                            THIDHI
                                          </small>

                                          <strong>
                                            {item.thidhi}
                                          </strong>
                                        </div>
                                      )}

                                      {/* UNIT RATE */}

                                      <div>
                                        <small>
                                          UNIT RATE
                                        </small>

                                        <strong>
                                          {money(
                                            item.unit_rate
                                          )}
                                        </strong>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                          </div>

                          {/* ===============================
                              PAYMENT SUMMARY
                          =============================== */}

                          <div className="pa-payment-summary">
                            <div>
                              <small>
                                PAYMENT STATUS
                              </small>

                              <span
                                className={`pa-status ${getStatusClass(
                                  order.payment_status
                                )}`}
                              >
                                {order.payment_status ||
                                  "PENDING"}
                              </span>
                            </div>

                            {order.payment_reference && (
                              <div>
                                <small>
                                  PAYMENT REFERENCE
                                </small>

                                <strong>
                                  {
                                    order.payment_reference
                                  }
                                </strong>
                              </div>
                            )}

                            <div>
                              <small>
                                BOOKING DATE & TIME
                              </small>

                              <strong>
                                {order.booking_datetime_display ||
                                  "-"}{" "}
                                IST
                              </strong>
                            </div>

                            <div className="pa-payment-grand-total">
                              <small>
                                GRAND TOTAL
                              </small>

                              <strong>
                                {money(
                                  order.total_amount
                                )}
                              </strong>
                            </div>
                          </div>

                          {/* CLOSE */}

                          <div className="pa-collapse-action">
                            <button
                              type="button"
                              onClick={() =>
                                toggleOrder(
                                  order.order_no
                                )
                              }
                            >
                              <i className="fa fa-chevron-up" />
                              Collapse Details
                            </button>
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}